import { useCallback, useMemo } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import {
  GrcNotify,
  NotifyType,
  PagedQuery,
  PagedResult,
  ServerPagingOptions,
  toQueryParams
} from '@grc/ui-package';
import {
  GetQuotesPage,
  QuotesPageError
} from '../../service/api/get-quotes-service';
import { QuoteTabKey } from '../../models/quotes/quote-tab.interface';
import { IDateRangeFilterValue } from '../../common/date-range-filter/date-range-filter.interface';
import { IQuoteGridRow } from '../../models/quote-management/quote-grid.interface';
import { useI18nState } from '../../stores/settings/language-context';
import { quoteTabsConfig } from './quote-management.config';
import { useQuoteEntities } from './useQuoteEntities';

// Toasts a failed page with the localized `message`. Stays quiet when
// fetchData already toasted it (400/409) or the request was cancelled.
export const createQuotesPageErrorNotifier =
  (message: string) => (error: unknown) => {
    if (error instanceof QuotesPageError && (error.notified || error.cancelled)) {
      return;
    }
    GrcNotify({ message, status: NotifyType.Error });
  };

// Server-side paging for one tab's grid against GET /v1/quotes: the tab sets
// `status`, the toolbar search box sets `quoteNumber` (contains) unless the
// Quote ID column filter supplies its own, and column filters/sort come from
// the grid. Origin/Destination are resolved per page. `fromDate`/`toDate`/
// `dateFilterType` are still sent as before; the backend ignores them (it is
// not known which date the page-level picker means). `refreshToken` is bumped
// after approve/reject so every open tab reloads from page 1.
export function useQuoteGridData(
  tab: QuoteTabKey,
  searchText: string,
  dateFilter: IDateRangeFilterValue,
  refreshToken = 0
) {
  const { localeData } = useI18nState();
  const { fetchData } = useGetApiData();
  const { enrichPage } = useQuoteEntities();

  const status = quoteTabsConfig.find(config => config.key === tab)?.status;
  const fromDate = dateFilter.fromDate.toISOString();
  const toDate = dateFilter.toDate.toISOString();
  const { dateFilterType } = dateFilter;

  const fetchPage = useCallback(
    async (query: PagedQuery): Promise<PagedResult<IQuoteGridRow>> => {
      if (status === undefined) return { items: [], totalItemCount: 0 };

      const params = toQueryParams(query, {
        status,
        quoteNumber: query.filters.quoteNumber ? undefined : searchText.trim(),
        fromDate,
        toDate,
        dateFilterType
      });
      const page = await GetQuotesPage(fetchData, params);
      return { ...page, items: await enrichPage(page.items) };
    },
    [fetchData, enrichPage, status, searchText, fromDate, toDate, dateFilterType]
  );

  const refreshKey = [
    status,
    searchText.trim(),
    fromDate,
    toDate,
    dateFilterType,
    refreshToken
  ].join('|');

  const onError = useMemo(
    () => createQuotesPageErrorNotifier(localeData.QuotesLoadingFailed),
    [localeData.QuotesLoadingFailed]
  );

  const serverPaging: ServerPagingOptions<IQuoteGridRow> = useMemo(
    () => ({ fetchPage, refreshKey, onError }),
    [fetchPage, refreshKey, onError]
  );

  return { serverPaging };
}

export default useQuoteGridData;
